import { useCallback, useMemo, useState } from "react";
import {
  CommonRawEventName,
  useEmitCoreCommonRawEvent,
} from "@b2bportal/core-tracking";
import type { AutocompleteState } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import type { HotelLocationAutocompleteProps } from "./HotelLocationAutocomplete";
import type { HotelLocationSuggestion } from "./types";
export const useHotelLocationAutocomplete = (
  props: HotelLocationAutocompleteProps
) => {
  const {
    id,
    value,
    label,
    onChange,
    onOpen,
    className,
    placeholder,
    defaultValue,
    useDialogOnMobile,
    onFocus,
  } = props;
  const [open, setOpen] = useState(false);

  const openDialog = useCallback(() => {
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  const { isDesktopAndUp } = useDeviceType();

  const emitSuggestionTapped = useEmitCoreCommonRawEvent(
    CommonRawEventName.SearchSuggestionTapped
  );

  const onChangeHandler = useCallback(
    (
      value: HotelLocationSuggestion | null,
      autocompleteState?: AutocompleteState<HotelLocationSuggestion>
    ) => {
      if (value != null) {
        onChange(value);
        if (autocompleteState != null) {
          const encryptedProperties =
            value.trackingPropertiesV2?.encryptedProperties;
          const encryptedPropertiesArray =
            encryptedProperties != null ? [encryptedProperties] : [];
          emitSuggestionTapped({
            properties: {
              location_type: value.categoryLabel ?? "",
              search_term: value.label,
              char_typed: autocompleteState.search,
              location_list_index: autocompleteState.options.findIndex(
                (item) => item === value
              ),
            },
            encryptedProperties: encryptedPropertiesArray,
          });
        }
      }
    },
    [onChange, emitSuggestionTapped]
  );

  return useMemo(() => {
    return {
      context: {
        id,
        value,
        label,
        className,
        placeholder,
        open,
        showLocationInput: isDesktopAndUp,
        defaultValue,
        useDialogOnMobile,
        mobileDialogTitle: "",
      },
      handlers: {
        onChange: onChangeHandler,
        onOpen,
        openDialog,
        closeDialog,
        onFocus,
      },
    };
  }, [
    id,
    value,
    label,
    className,
    placeholder,
    open,
    isDesktopAndUp,
    defaultValue,
    useDialogOnMobile,
    onChangeHandler,
    onOpen,
    openDialog,
    closeDialog,
    onFocus,
  ]);
};
